/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Quick Theme Switcher Component for Dashboard Header (Task 10.7.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../../context/ThemeProvider';
import { useI18nStore } from '../../i18n';

export interface ThemeSwitcherProps {
  className?: string;
}

export const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({ className = '' }) => {
  const { isDark, setThemeMode } = useTheme();
  const { t } = useI18nStore();

  const handleToggle = () => {
    setThemeMode(isDark ? 'light' : 'dark');
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      className={`group relative p-2 rounded-xl border border-surface-border bg-surface hover:bg-muted/70 text-foreground-muted transition-all duration-200 shadow-sm active:scale-95 ${className}`}
      title={t('header.toggleTheme')}
      aria-label={t('header.toggleTheme')}
      data-testid="quick-theme-toggle"
    >
      {isDark ? (
        <Sun className="w-4 h-4 text-amber-400 group-hover:rotate-45 transition-transform duration-300" />
      ) : (
        <Moon className="w-4 h-4 text-foreground-muted group-hover:text-primary transition-colors duration-200" />
      )}
    </button>
  );
};
